import React from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorMessages, errorKind, isNotFound } from 'config/apiErrors';

interface FetchFailedProps {
  /** 取得に失敗したときのエラー（axios のエラーなど）。 */
  error: unknown;
  /** 同じ条件で取り直す。 */
  onRetry: () => void;
  /** 見出しの文（既定は「一覧を読み込めませんでした」）。 */
  message?: string;
  /** EV-318。狭い場所（左のメニューの下のプロフィールなど）に置くときの詰めた表示。文言は同じ。 */
  compact?: boolean;
  /** EV-322。欄の注記として使うときの id（欄の aria-describedby から結ぶ）。 */
  id?: string;
  /**
   * 404 のときの理由（既定は「見つかりませんでした（削除された可能性があります）。」）。2026-10-26 のステージングの
   * 確かめ: 削除済みの会社の詳細で、サーバーの英語の文（No Company matches the given query.）がそのまま出ていた。
   */
  notFoundMessage?: string;
}

/**
 * ADM-15-AC1。一覧・詳細の取得に失敗したことを、空の一覧と区別して示す。
 *
 * 失敗の種類（通信断・権限・サーバー障害）を添え、権限が無いとき以外は再読み込みを出す
 * （権限が無いときは何度試しても同じ結果になるため）。
 */
const FetchFailed: React.FC<FetchFailedProps> = ({ error, onRetry, message, compact = false, id, notFoundMessage }) => {
  const { t } = useTranslation('common');
  const kind = errorKind(error);
  // 404 は、サーバーの文（Django の get_object_or_404 の英語の文・DRF の「Not found.」）を出さず、画面の言語の文にする。
  const notFound = kind === 'other' && isNotFound(error);
  const reason =
    kind === 'network'
      ? t('ui.error-network', { ns: 'common' })
      : kind === 'forbidden'
      ? t('ui.error-forbidden', { ns: 'common' })
      : kind === 'server'
      ? t('ui.error-server', { ns: 'common' })
      : notFound
      ? notFoundMessage ?? t('ui.error-not-found', { ns: 'common' })
      : null;

  // 2026-10-26。文字は gray-700（以前の gray-500 は、運営の画面の灰色の背景でコントラストが 4.5 に届かなかった。axe）。
  return (
    <div
      id={id}
      role="alert"
      className={
        compact
          ? 'flex flex-col items-center gap-1 p-2 text-caption text-gray-700'
          : 'flex flex-col items-center gap-2 py-8 text-gray-700'
      }
    >
      <p>{message ?? t('ui.fetch-failed', { ns: 'common' })}</p>
      {reason && <p>{reason}</p>}
      {kind === 'other' && !notFound && apiErrorMessages(error, '').filter(Boolean).map((detail, index) => <p key={index}>{detail}</p>)}
      {kind !== 'forbidden' && (
        <button
          type="button"
          className={compact ? 'rounded-md border px-3 py-1 text-primary/300' : 'rounded-md border px-4 py-2 text-primary/300'}
          onClick={onRetry}
        >
          {t('ui.retry', { ns: 'common' })}
        </button>
      )}
    </div>
  );
};

export default FetchFailed;
